import React, {
	createContext,
	Dispatch,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { UpgradeProps } from '@/components/AccountPanel/components/Upgrade/Upgrade';
import {
	useCheckSubscriptionStatusQuery,
	useGetSubscriptionSessionMutation,
	useLinkTeamChargebeeCustomerMutation,
} from '@/graphql';
import { FetchError } from '@/graphql/codegen-fetcher';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useEnvironment } from '@/providers/EnvironmentProvider';
import { useSnackbar } from 'notistack';
import { isNil } from 'ramda';

import { User, useAuth } from '../AuthProvider';
import { LoadingResource, useLoading } from '../LoadingProvider';
import { TeamContext } from '../TeamProvider';

interface ISubscriptionContext {
	status: boolean;
	statusLoading: boolean;
	checkStatus: () => void;
	chargebeeInstance: any;
	cart: any;
	loadingChargebeeSession: boolean;
	portalSessionRes: string;
	paidModalOpen: boolean;
	setPaidModalOpen: Dispatch<boolean>;
}

const SubscriptionContext = createContext<ISubscriptionContext>({
	status: false,
	statusLoading: false,
	checkStatus: () => {},
	chargebeeInstance: null,
	cart: null,
	loadingChargebeeSession: false,
	portalSessionRes: '',
	paidModalOpen: false,
	setPaidModalOpen: () => {},
});

export function useSubscription(): ISubscriptionContext {
	const context = useContext(SubscriptionContext);
	if (isNil(context)) {
		throw new Error(
			`useSubscription must be used within an SubscriptionProvider`,
		);
	}

	return context;
}

export const SubscriptionProvider: React.FC<any> = (props) => {
	const { enqueueSnackbar } = useSnackbar();
	const { setLoaded } = useLoading();
	const { updateUser } = useAuth();
	// Held in a ref so the checkout callbacks below don't re-initialise
	// whenever the auth provider re-creates updateUser.
	const updateUserRef = React.useRef(updateUser);
	updateUserRef.current = updateUser;
	const [portalSessionRes, setPortalSessionRes] = useState('');
	const [paidModalOpen, setPaidModalOpen] = useState(false);

	const {
		data: statusRes,
		isLoading,
		fetchStatus: subscriptionQueryFetchStatus,
		refetch: checkStatus,
	} = useCheckSubscriptionStatusQuery(
		{},
		{
			refetchOnWindowFocus: false,
		},
	);
	const isSubscriptionStatusLoading =
		isLoading && subscriptionQueryFetchStatus !== 'idle';
	const status = useMemo(
		() => statusRes?.checkAndSetChargebeeSubscription || false,
		[statusRes],
	);

	const {
		mutateAsync: mutateChargebeePortalSession,
		error: chargebeePortalError,
		isLoading: chargebeePortalLoading,
	} = useGetSubscriptionSessionMutation<FetchError>();

	const { mutateAsync: mutateLinkTeamChargebeeCustomer } =
		useLinkTeamChargebeeCustomerMutation<FetchError>();

	const fireSubscriptionCheckoutLinked = useAnalyticsEvent(
		'Subscription_Checkout_Linked',
	);

	useEffect(() => {
		const execute = async () => {
			if (!status) {
				// console.log(
				// 	'Subscription status inactive so no need to check session',
				// );
				return;
			}
			try {
				// console.log('Subscription status active, checking session');
				const res = await mutateChargebeePortalSession({});
				if (res.getChargebeePortalSession) {
					setPortalSessionRes(
						JSON.parse(res.getChargebeePortalSession),
					);
				}
			} catch (err) {
				console.log(
					'Error while getting chargebee portal session',
					err,
				);
				enqueueSnackbar(
					'Could not authorize subscription management. Please contact support',
					{ variant: 'warning' },
				);
			}
		};
		execute();
	}, [mutateChargebeePortalSession, status, enqueueSnackbar]);

	const getChargebeeSession = useCallback(
		async () => Promise.resolve(portalSessionRes),
		[portalSessionRes],
	);

	const [chargebeeInstance, setChargebeeInstance] = useState({
		setPortalSession: () => {
			console.log('setPortalSession not initialised yet');
		},
		createChargebeePortal: () => {
			console.log('createChargebeePortal not initialised yet');
			return {
				open: (options: any) => {
					console.log(
						'createChargebeePortal.open not initialised yet',
					);
				},
			};
		},
	});
	const [cart, setCart] = useState({
		replaceProduct: () => console.log('replaceProduct not initialised yet'),
		proceedToCheckout: () =>
			console.log('proceedToCheckout not initialised yet'),
	});

	const { integrations } = useEnvironment();
	const chargebeeDomain = integrations.chargebee.domain;

	useEffect(() => {
		Chargebee.init({
			site: chargebeeDomain,
			isItemsModel: true,
			iframeOnly: true,
		});
	}, [chargebeeDomain]);

	// const { data, isLoading, error } = useGetSubscriptionSessionQuery();

	// useEffect(() => {
	// 	if (isLoading) {
	// 		return setLoadingChargebeeSession(true);
	// 	}
	// 	if (data) {
	// 		try {
	// 			const chargebeeSession = JSON.parse(
	// 				data.getChargebeePortalSession,
	// 			);
	// 			console.log('chargebeeSession', chargebeeSession);
	// 			setPortalSessionRes(chargebeeSession);
	// 		} catch (err) {
	// 			console.log('chargebeeSession', err);
	// 		}
	// 	}

	// 	setLoadingChargebeeSession(false);
	// }, [data, isLoading, error]);

	useEffect(() => {
		if (isSubscriptionStatusLoading) {
			return;
		}

		setLoaded(LoadingResource.SUBSCRIPTION);
		const _chargebeeInstance = Chargebee.getInstance();

		// Setting global reference
		setChargebeeInstance(_chargebeeInstance);
		setCart(_chargebeeInstance.getCart());

		// Configuring instance settings
		_chargebeeInstance.setPortalSession(getChargebeeSession);
		_chargebeeInstance.setCheckoutCallbacks(function (cart: any) {
			return {
				success: function (hpid: string) {
					console.log('success', hpid);
				},
				step: async function (step: string) {
					console.log('step', step);
					if (step === 'thankyou_screen') {
						// remove possible query string params
						// they are passed from here: client\src\providers\AuthProvider\AuthProvider.tsx
						window.history.pushState(
							{},
							document.title,
							window.location.pathname,
						);
						try {
							await mutateLinkTeamChargebeeCustomer({});
							fireSubscriptionCheckoutLinked();
							// The server may have filled in the user's name from
							// checkout; an empty update returns the latest user.
							await updateUserRef.current({ input: {} }).catch(() => {});
						} catch (err) {
							console.log(
								'Error while linking team chargebee customer',
								err,
							);
							enqueueSnackbar(
								'Could not confirm your subscription. Please contact support',
								{ variant: 'warning' },
							);
						}
						checkStatus();
					}
				},
			};
		});
	}, [
		isLoading,
		isSubscriptionStatusLoading,
		checkStatus,
		getChargebeeSession,
		setLoaded,
		mutateLinkTeamChargebeeCustomer,
		fireSubscriptionCheckoutLinked,
		enqueueSnackbar,
	]);

	return (
		<SubscriptionContext.Provider
			value={{
				status,
				statusLoading: isSubscriptionStatusLoading,
				checkStatus,
				chargebeeInstance,
				cart,
				portalSessionRes,
				loadingChargebeeSession: chargebeePortalLoading,
				paidModalOpen,
				setPaidModalOpen: setPaidModalOpen,
			}}
			{...props}
		>
			{props.children}
		</SubscriptionContext.Provider>
	);
};

type OpenCheckoutOptions = {
	cbInstance: any;
	cart: any;
	user: User;
	team: TeamContext['team'];
	period: string;
	currency: string;
	planPriceIdPrefix?: string;
	couponId?: string;
	onClose?: () => void;
	popDisplayState?: UpgradeProps['popDisplayState'];
};

export const openCheckout = ({
	cbInstance,
	cart,
	user,
	team,
	period,
	currency,
	planPriceIdPrefix = 'Live-Test',
	couponId,
	onClose,
	popDisplayState,
}: OpenCheckoutOptions) => {
	if (onClose) {
		onClose();
	}
	if (popDisplayState) {
		popDisplayState();
	}
	const constructPlanPriceId = `${planPriceIdPrefix}-${currency}-${period}`; // Plan price point ID is used to identify the product
	const planPriceQuantity = 1;
	const product = cbInstance.initializeProduct(
		constructPlanPriceId,
		planPriceQuantity,
	);
	cart.replaceProduct(product);

	const first_name =
		user?.displayName?.split(' ').slice(0, -1).join(' ') || '';
	const last_name = user?.displayName?.split(' ').slice(-1).join(' ') || '';
	const email = user?.email || '';

	cart.setCustomer({ first_name, last_name, email });

	// Adding an addon
	// product.addAddon({
	// 	id: "silver-pass-USD-monthly",  // Addon price point ID
	// 	quantity: 2
	// })

	if (couponId) {
		product.addCoupon(couponId);
	}

	// Dynamically changing Plan quantity using setPlanQuantity
	// product.setPlanQuantity(planPriceQuantity);

	// Removing Addons using removeAddon
	// product.removeAddon("silver-pass-USD-monthly"); // Addon price point ID

	// Passing values for custom fields
	if (!team || !team.id) {
		console.error('Team ID not found');
		throw new Error('Team ID not found');
	}
	// Note that this has to be added https://www.chargebee.com/docs/2.0/custom_fields.html#custom-fields-for-subscriptions
	product.setCustomData({ cf_team_id: team.id });

	// Opening the checkout
	cart.proceedToCheckout();
};

export default SubscriptionProvider;
